import React, { memo, useMemo } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getStorageUrl, getThumbnailUrl, ThumbnailSize } from "@/lib/storage";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  /** Avatar path from profiles.avatar_url (not full URL) */
  avatarPath: string | null | undefined;
  /** User's full name for fallback initial */
  fullName: string | null | undefined;
  /** Size class (h-8 w-8, h-10 w-10, etc.) */
  className?: string;
  /** Fallback background class */
  fallbackClassName?: string;
  /** 
   * Use thumbnail size for optimized loading. 
   * Set to a pixel size (48, 64, 128, 256) to use Supabase image transforms.
   * Omit or set to undefined for full-size image.
   */
  thumbnailSize?: ThumbnailSize;
}

/**
 * Memoized avatar component that efficiently handles storage URLs.
 * Prevents unnecessary re-renders and URL recalculations.
 * Supports thumbnail sizes for bandwidth optimization in lists.
 */
export const UserAvatar = memo(function UserAvatar({
  avatarPath,
  fullName,
  className = "h-10 w-10",
  fallbackClassName = "bg-primary text-primary-foreground",
  thumbnailSize,
}: UserAvatarProps) {
  // Memoize the URL derivation to prevent recalculation on every render
  const avatarUrl = useMemo(() => {
    if (!avatarPath) return null;
    
    // Use thumbnail URL if size specified, otherwise full URL
    if (thumbnailSize) {
      return getThumbnailUrl("avatars", avatarPath, thumbnailSize);
    }
    
    // If already a full URL (legacy), use as-is
    if (avatarPath.startsWith("http")) return avatarPath;
    // Generate URL from path
    return getStorageUrl("avatars", avatarPath);
  }, [avatarPath, thumbnailSize]);

  const initial = useMemo(() => {
    return fullName?.charAt(0)?.toUpperCase() || "?";
  }, [fullName]);

  return (
    <Avatar className={cn("shrink-0", className)}>
      <AvatarImage src={avatarUrl || undefined} alt={fullName || "User avatar"} />
      <AvatarFallback className={cn("text-xs", fallbackClassName)}>
        {initial}
      </AvatarFallback>
    </Avatar>
  );
});

UserAvatar.displayName = "UserAvatar";
